import type { TextFieldProps } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { MergeTagLookup } from "./merge-tag-lookup";
import type { TagGroup } from "./types";

interface MergeTagAutocompleteProps {
  filterTags: (x: string) => TagGroup;
  onChange: (text: string) => void;
  renderInput: (params: TextFieldProps) => React.ReactElement;
  defaultValue?: string;
}

const openingTag = "{{";
const closingTag = "}}";

export const MergeTagAutocomplete = ({
  filterTags,
  onChange,
  renderInput,
  defaultValue = "",
}: MergeTagAutocompleteProps): React.ReactElement => {
  const inputRef = useRef<HTMLDivElement>(null);

  const [value, setValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const lastWord = getLastWord(value);
    if (!lastWord.startsWith(openingTag)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOpen(false);
      return;
    }

    if (lastWord.includes(closingTag)) {
      setOpen(false);
      return;
    }
    setOpen(true);
  }, [value]);

  const handleSelection = useCallback(
    (tag: string): void => {
      const words = value.trim().split(" ");
      words[words.length - 1] = `${openingTag}${tag}${closingTag}`;
      const text = words.join(" ") + " ";
      setValue(text);
      onChange(text);
      inputRef.current?.focus?.();
    },
    [value, onChange],
  );

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setValue(e.target.value);
      onChange(e.target.value);
    },
    [onChange],
  );

  return (
    <>
      {renderInput({ value, onChange: handleInputChange, inputRef })}
      {open && (
        <MergeTagLookup
          filterTags={filterTags}
          open={open}
          // eslint-disable-next-line react-hooks/refs
          anchorEl={inputRef.current}
          onSelection={handleSelection}
          onClose={() => setOpen(false)}
          defaultValue={value}
        />
      )}
    </>
  );
};

function getLastWord(text: string): string {
  const words = text.trim().split(" ");

  return words[words.length - 1] || "";
}
